import { Component, OnInit } from '@angular/core';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ItemCommandeEntite } from '../../entites/item-commande';
import { Emplacement } from '../../entites/emplacement';
import { Requisition } from '../../entites/requisition';
import { EmplacementService } from '../../services/emplacement-service';
import { RequisitionService } from '../../services/requisition-service';
import { CommandeService } from '../../services/commande-service';
import { ProfilService } from '../../services/profil-service';
import { AppMainComponent } from '../../app.main.component';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

interface EmplacementGroup {
    emplacement: Emplacement;
    items: ItemCommandeEntite[];
    selectedRequisition?: Requisition;
    createNew: boolean;
    requisitionChoice: 'existing' | 'new';
    newRequisitionData?: {
        transporteur?: string;
        camion?: string;
        chauffeur?: string;
    };
}

interface StepperResult {
    success: boolean;
    message: string;
    emplacement: string;
    itemsProcessed: number;
    requisitionId?: number;
    requisitionNumero?: string;
    isNewRequisition?: boolean;
}

@Component({
    selector: 'app-ajout-requisition-stepper',
    templateUrl: './ajout-requisition-stepper.component.html',
    styleUrls: ['./ajout-requisition-stepper.component.scss'],
    providers: [MessageService, ConfirmationService]
})
export class AjoutRequisitionStepperComponent implements OnInit {

    items: ItemCommandeEntite[] = [];
    emplacementGroups: EmplacementGroup[] = [];
    activeIndex: number = 0;

    // Stepper state
    currentGroup: EmplacementGroup;
    availableRequisitions: Requisition[] = [];
    loadingRequisitions: boolean = false;

    // New requisition form data
    emplacements: Emplacement[] = [];

    // Processing state
    processing: boolean = false;
    results: StepperResult[] = [];
    showResults: boolean = false;
    currentProcessingGroup: string = '';
    processingProgress: number = 0;

    constructor(
        public ref: DynamicDialogRef,
        public config: DynamicDialogConfig,
        private messageService: MessageService,
        private confirmationService: ConfirmationService,
        private emplacementService: EmplacementService,
        private requisitionService: RequisitionService,
        private commandeService: CommandeService,
        private profilService: ProfilService,
        private router: Router
    ) {
        this.items = this.config.data?.items || [];
    }

    ngOnInit() {
        this.initializeData();
        this.groupItemsByEmplacement();
    }

    private async initializeData() {
        try {
            const emplacementsResult = await this.emplacementService.Obtenir();
            if (emplacementsResult.estUnSucces) {
                this.emplacements = emplacementsResult.valeur;
            }
        } catch (error) {
            console.error('Error loading initial data:', error);
            this.messageService.add({
                severity: 'error',
                summary: 'Erreur',
                detail: 'Erreur lors du chargement des données initiales'
            });
        }
    }

    private groupItemsByEmplacement() {
        const groups = new Map<number, EmplacementGroup>();

        this.items.forEach(item => {
            const emplacementId = item.emplacement.emplacementId;

            if (!groups.has(emplacementId)) {
                groups.set(emplacementId, {
                    emplacement: item.emplacement,
                    items: [],
                    createNew: true,
                    requisitionChoice: 'new',
                    newRequisitionData: {
                        transporteur: undefined,
                        camion: undefined,
                        chauffeur: undefined
                    }
                });
            }

            groups.get(emplacementId)!.items.push(item);
        });

        this.emplacementGroups = Array.from(groups.values());

        if (this.emplacementGroups.length > 0) {
            this.currentGroup = this.emplacementGroups[0];
            this.loadRequisitionsForCurrentGroup();
        }
    }

    private loadRequisitionsForCurrentGroup() {
        if (!this.currentGroup) return;

        this.loadingRequisitions = true;
        this.availableRequisitions = [];

        this.emplacementService.ObtenirRequisitionsEmplacement(this.currentGroup.emplacement.emplacementId)
            .subscribe({
                next: (requisitions) => {
                    this.availableRequisitions = requisitions || [];
                    this.loadingRequisitions = false;
                },
                error: (error) => {
                    console.error('Error loading requisitions:', error);
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Erreur',
                        detail: 'Erreur lors du chargement des réquisitions'
                    });
                    this.loadingRequisitions = false;
                }
            });
    }

    onStepChange(event: any) {
        this.navigateToStep(event.index);
    }

    navigateToStep(stepIndex: number) {
        if (stepIndex >= 0 && stepIndex <= this.emplacementGroups.length) {
            this.activeIndex = stepIndex;
            if (stepIndex < this.emplacementGroups.length) {
                this.currentGroup = this.emplacementGroups[stepIndex];
                this.loadRequisitionsForCurrentGroup();
            }
        }
    }

    onRequisitionChoiceChange() {
        if (this.currentGroup) {
            if (this.currentGroup.requisitionChoice === 'existing') {
                this.currentGroup.createNew = false;
            } else if (this.currentGroup.requisitionChoice === 'new') {
                this.currentGroup.createNew = true;
            }
        }
    }

    canProceedToNext(): boolean {
        if (!this.currentGroup) return false;

        if (this.currentGroup.createNew) {
            return this.validateNewRequisitionData();
        } else {
            return this.currentGroup.selectedRequisition != null;
        }
    }

    canNavigateToStep(stepIndex: number): boolean {
        // Allow navigation to any step
        return true;
    }

    private validateNewRequisitionData(): boolean {
        if (!this.currentGroup?.newRequisitionData) return false;

        const data = this.currentGroup.newRequisitionData;
        return !!(data.transporteur && data.camion && data.chauffeur);
    }

    nextStep() {
        if (this.activeIndex < this.emplacementGroups.length - 1) {
            this.navigateToStep(this.activeIndex + 1);
        } else {
            this.processAllGroups();
        }
    }

    previousStep() {
        if (this.activeIndex > 0) {
            this.navigateToStep(this.activeIndex - 1);
        }
    }

    private async processAllGroups() {
        this.processing = true;
        this.results = [];

        try {
            // Process groups sequentially to avoid backend deadlocks
            for (let i = 0; i < this.emplacementGroups.length; i++) {
                const group = this.emplacementGroups[i];
                this.currentProcessingGroup = group.emplacement.nom;
                this.processingProgress = Math.round(((i + 1) / this.emplacementGroups.length) * 100);

                const result = await this.processGroup(group);
                this.results.push(result);

                // Small delay between processing to prevent overwhelming the backend
                await new Promise(resolve => setTimeout(resolve, 500));
            }

            this.showResults = true;
            this.activeIndex = this.emplacementGroups.length; // Move to results step

        } catch (error) {
            console.error('Error processing groups:', error);
            this.messageService.add({
                severity: 'error',
                summary: 'Erreur',
                detail: 'Erreur lors du traitement des groupes'
            });
        } finally {
            this.processing = false;
            this.currentProcessingGroup = '';
            this.processingProgress = 0;
        }
    }

    private async processGroup(group: EmplacementGroup): Promise<StepperResult> {
        try {
            let requisitionId: number;
            let createResult: any = null;
            const profilCourant = this.profilService.ProfilCourant();

            if (group.createNew && group.newRequisitionData) {
                // Ensure items have demandeur, quantite and departement for EnvoyerPreCommandeEnChantier
                const itemsForRequest = group.items.map(ic => ({
                    ...ic,
                    demandeur: ic.demandeur || profilCourant,
                    quantite: ic.quantite ?? ic.quantiteDemande,
                    departement: ic.departement || { nom: 'Non assigné' }
                }));

                const requete = {
                    transporteur: group.newRequisitionData.transporteur,
                    camion: group.newRequisitionData.camion,
                    chauffeur: group.newRequisitionData.chauffeur,
                    items: itemsForRequest
                };

                try {
                    const response = await firstValueFrom(
                        this.commandeService.EnvoyerPrecommandeEnChantier(requete)
                    );
                    createResult = { valeur: response.body };
                } catch (err: any) {
                    return {
                        success: false,
                        message: err?.error || err?.message || 'Erreur lors de la création de la réquisition',
                        emplacement: group.emplacement.nom,
                        itemsProcessed: 0
                    };
                }

                requisitionId = createResult.valeur.requisitionId;
            } else if (group.selectedRequisition) {
                requisitionId = group.selectedRequisition.requisitionId;
            } else {
                return {
                    success: false,
                    message: 'Aucune réquisition sélectionnée ou créée',
                    emplacement: group.emplacement.nom,
                    itemsProcessed: 0
                };
            }

            let successCount: number;
            if (group.createNew) {
                // EnvoyerPreCommandeEnChantier already added all items
                successCount = group.items.length;
            } else {
                // Transfer selected command items to the existing requisition in one call.
                const transfertResult = await this.requisitionService.TransfertCommande({
                    employe: this.profilService.ProfilCourant(),
                    requisitionId: requisitionId,
                    itemsCommande: group.items
                });

                if (!transfertResult.estUnSucces) {
                    const messageErreur = transfertResult.messages?.map(x => x.description).join(' | ')
                        || 'Erreur lors du transfert des items de commande';
                    return {
                        success: false,
                        message: messageErreur,
                        emplacement: group.emplacement.nom,
                        itemsProcessed: 0
                    };
                }

                successCount = transfertResult.valeur?.itemCommandeSupprimes?.length ?? group.items.length;
            }

            return {
                success: successCount === group.items.length,
                message: `${successCount}/${group.items.length} items ajoutés avec succès`,
                emplacement: group.emplacement.nom,
                itemsProcessed: successCount,
                requisitionId: requisitionId,
                requisitionNumero: group.createNew ?
                    (createResult?.valeur?.numero?.toString() || 'N/A') :
                    (group.selectedRequisition?.numero?.toString() || 'N/A'),
                isNewRequisition: group.createNew
            };

        } catch (error) {
            console.error('Error processing group:', error);
            return {
                success: false,
                message: 'Erreur lors du traitement du groupe',
                emplacement: group.emplacement.nom,
                itemsProcessed: 0
            };
        }
    }

    getStepTitle(index: number): string {
        if (index < this.emplacementGroups.length) {
            const group = this.emplacementGroups[index];
            return `${group.emplacement.nom} (${group.items.length} items)`;
        } else {
            return 'Résultats';
        }
    }

    getSuccessfulResults(): StepperResult[] {
        return this.results.filter(r => r.success);
    }

    getFailedResults(): StepperResult[] {
        return this.results.filter(r => !r.success);
    }

    getTotalItemsProcessed(): number {
        return this.results.reduce((total, result) => total + result.itemsProcessed, 0);
    }

    getStepItems() {
        const steps = this.emplacementGroups.map((group, index) => ({
            label: this.getStepTitle(index)
        }));

        // Add results step
        steps.push({
            label: 'Résultats'
        });

        return steps;
    }

    close() {
        this.ref.close({
            success: this.results.some(r => r.success),
            results: this.results
        });
    }

    navigateToRequisition(requisitionId: number) {
        this.ref.close();
        this.router.navigateByUrl(`/transport/${requisitionId}`);
    }

    cancel() {
        if (this.processing) {
            // If processing is ongoing, show confirmation
            this.messageService.add({
                severity: 'warn',
                summary: 'Attention',
                detail: 'Un traitement est en cours. Veuillez patienter.'
            });
            return;
        }
        this.ref.close();
    }
}